import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { ApiUnavailableBanner } from '../components/ApiUnavailableBanner';
import { AdSlot } from '../components/AdSlot';
import { EmptyResults } from '../components/EmptyResults';
import { ReleaseCard } from '../components/ReleaseCard';
import type { RenderableContentItem } from '../components/ContentCard';
import SeoHead from '../components/SeoHead';
import { brandingFixture } from '../lib/branding/branding-fixture';
import { searchReleases } from '../lib/api/content-api-client';
import { getPublicWebsiteConfig } from '../lib/api/config';
import type { ApprovedContentItem } from '@dvgup/contracts/generated/approved-content-api';

/**
 * Games landing surface (task 6.8, public-website spec).
 *
 * `<GamesPage>` at route `/games` renders:
 *   - A search input at the top.
 *   - A reverse-chronological grid of release cards (`<ReleaseCard>`),
 *     reusing the same card the Daily page renders.
 *   - 10 results per visible page, with infinite scroll loading the
 *     next slice when an `IntersectionObserver` sentinel scrolls
 *     into view. New API windows are fetched on demand once the
 *     current window is fully rendered.
 *   - Future dates are filtered out — the listing only shows
 *     releases dated today or earlier.
 *   - One sponsored card (`<AdSlot variant="release">`) interleaved
 *     after every 10th release.
 *
 * Data source: until `smithy-api-layer` follow-up A wires the real
 * `SearchGamesV1` (it currently rejects empty `q`), we power the
 * page from `searchReleases` (an alias around
 * `GetApprovedContentV1 {type:"release"}` exposed by the SPA's
 * content API client). Each release becomes a card via the same
 * encoded-body envelope the Daily page consumes — title, image,
 * platforms, purchaseUrl, gameId all surface in the renderable
 * content shape.
 *
 * Search is applied client-side as a case-insensitive substring
 * match against the encoded-body title + body copy. Empty input
 * shows the full reverse-chronological list (the documented R20.7
 * default-landing experience).
 */

const PAGE_SIZE = 10;
const SPONSORED_EVERY = 10;

interface GamesPageState {
  /** Every release row fetched so far across all API windows. */
  readonly fetchedItems: ReadonlyArray<ApprovedContentItem>;
  /** How many of `fetchedItems` are currently rendered. */
  readonly visibleCount: number;
  /** Next API window to fetch (1-indexed). */
  readonly nextWindow: number;
  /** Whether the most-recent API window said `hasMore`. */
  readonly apiHasMore: boolean;
  readonly status: 'idle' | 'loading' | 'succeeded' | 'failed';
  readonly error: string | null;
}

const INITIAL_STATE: GamesPageState = {
  fetchedItems: [],
  visibleCount: 0,
  nextWindow: 1,
  apiHasMore: true,
  status: 'idle',
  error: null,
};

/** Resolve `?q=` query string param at mount. */
function readQueryFromSearch(search: URLSearchParams): string {
  return search.get('q') ?? '';
}

/** Pluck the `gameId` (or fall back to wire id) for the Learn-more link. */
function pickLearnMoreUrl(item: RenderableContentItem): string | undefined {
  const encodedGameId = (item as { gameId?: unknown }).gameId;
  if (typeof encodedGameId === 'string' && encodedGameId.length > 0) {
    return `/games/${encodedGameId}`;
  }
  if (typeof item.id === 'string' && item.id.length > 0) {
    return `/games/${item.id}`;
  }
  return undefined;
}

/** Pluck the first non-empty affiliate URL from a release item. */
function pickPurchaseUrl(item: RenderableContentItem): string | undefined {
  const link = item.affiliateLinks?.[0];
  return typeof link?.url === 'string' && link.url.length > 0 ? link.url : undefined;
}

/**
 * `<GamesPage>` — route `/games`. Manages a single page-state
 * record (fetchedItems, visibleCount, nextWindow, apiHasMore,
 * status) and an `IntersectionObserver` sentinel that triggers
 * `loadNext` whenever the bottom of the list scrolls into view.
 */
export default function GamesPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [input, setInput] = useState(() => readQueryFromSearch(searchParams));
  const [state, setState] = useState<GamesPageState>(INITIAL_STATE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Reset the page state whenever the query changes so the next
  // load fetches window 1 against the new query.
  useEffect(() => {
    setState(INITIAL_STATE);
  }, [input]);
  // Reflect the current input into `?q=` so the URL stays
  // bookmarkable / refresh-survivable.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        if (input.trim()) updated.set('q', input.trim());
        else updated.delete('q');
        return updated;
      },
      { replace: true },
    );
  }, [input, setSearchParams]);
  const loadNext = useCallback(async () => {
    setState((prev) => loadNextSetup(prev));
    // The setState above schedules a state change but doesn't
    // surface the post-state synchronously. Read state through a
    // ref-via-closure: the second setState below operates on the
    // freshest `prev` and decides whether a network call is needed.
    const config = getPublicWebsiteConfig();
    let needsNetwork = false;
    let windowToFetch = 0;
    setState((prev) => {
      // Local-pagination path: there are still un-rendered fetched
      // items; bump the visible count and stay in 'succeeded'.
      if (prev.visibleCount < prev.fetchedItems.length) {
        return {
          ...prev,
          status: 'succeeded',
          visibleCount: Math.min(
            prev.fetchedItems.length,
            prev.visibleCount + PAGE_SIZE,
          ),
        };
      }
      // No more API windows to fetch — settle in 'succeeded'.
      if (!prev.apiHasMore && prev.fetchedItems.length > 0) {
        return { ...prev, status: 'succeeded' };
      }
      // Network path: fetch the next API window outside the
      // setState callback (we set the flags here and continue
      // below).
      needsNetwork = true;
      windowToFetch = prev.nextWindow;
      return { ...prev, status: 'loading', error: null };
    });
    if (!needsNetwork) return;
    try {
      const result = await searchReleases(
        { page: windowToFetch, q: input },
        { baseUrl: config.publicApiInvokeUrl, apiKey: config.publicApiKey },
      );
      setState((prev) => mergeWindow(prev, result.items, result.hasMore));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState((prev) => ({ ...prev, status: 'failed', error: message }));
    }
  }, [input]);
  // Initial fetch on mount or query change.
  useEffect(() => {
    if (state.status === 'idle') void loadNext();
  }, [state.status, loadNext]);
  // Wire the IntersectionObserver to call `loadNext` when the
  // sentinel scrolls into the viewport.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (state.status === 'loading') return;
    if (!state.apiHasMore && state.visibleCount >= state.fetchedItems.length) {
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) void loadNext();
    });
    obs.observe(node);
    return () => obs.disconnect();
  }, [state.status, state.apiHasMore, state.visibleCount, state.fetchedItems.length, loadNext]);
  return renderGamesBody({ input, setInput, state, sentinelRef });
}

/** Mark loading; preserves all other state fields. */
function loadNextSetup(prev: GamesPageState): GamesPageState {
  if (prev.status === 'loading') return prev;
  return prev;
}

/**
 * Merge a freshly-fetched API window's items into the state.
 * Appends only items not already present (de-duplicated by `id`),
 * advances the visible count by one page, and updates `apiHasMore`
 * + `nextWindow` for the next call.
 */
function mergeWindow(
  prev: GamesPageState,
  newItems: ReadonlyArray<ApprovedContentItem>,
  apiHasMore: boolean,
): GamesPageState {
  const seen = new Set(prev.fetchedItems.map((i) => i.id ?? '').filter(Boolean));
  const additions = newItems.filter((i) => !seen.has(i.id ?? ''));
  const merged = [...prev.fetchedItems, ...additions];
  return {
    fetchedItems: merged,
    visibleCount: Math.min(merged.length, prev.visibleCount + PAGE_SIZE),
    nextWindow: prev.nextWindow + 1,
    apiHasMore,
    status: 'succeeded',
    error: null,
  };
}

interface GamesGridProps {
  readonly items: ReadonlyArray<ApprovedContentItem>;
}

/**
 * Render the items as `<ReleaseCard>` rows in the same 2-column
 * grid the Daily page uses, interleaving an `<AdSlot
 * variant="release">` after every `SPONSORED_EVERY`-th item. When
 * the visible list is shorter than `SPONSORED_EVERY`, a single
 * sponsored card is appended at the tail so the page always
 * surfaces at least one sponsored placement per the operator's
 * "minimum one sponsored card per list, then one per ten"
 * requirement.
 */
function GamesGrid({ items }: GamesGridProps): JSX.Element {
  const renderable = items as ReadonlyArray<RenderableContentItem>;
  const tiles: Array<JSX.Element> = [];
  let injectedAdAfterTen = false;
  renderable.forEach((item, index) => {
    tiles.push(
      <ReleaseCard
        key={item.id ?? `release-${index}`}
        item={item}
        purchaseUrl={pickPurchaseUrl(item)}
        learnMoreUrl={pickLearnMoreUrl(item)}
      />,
    );
    const isMultipleOfBatch = (index + 1) % SPONSORED_EVERY === 0;
    if (isMultipleOfBatch) {
      tiles.push(<AdSlot key={`ad-${index}`} slot={1} variant="release" />);
      injectedAdAfterTen = true;
    }
  });
  // Guarantee one sponsored card on short lists.
  if (renderable.length > 0 && !injectedAdAfterTen) {
    tiles.push(<AdSlot key="ad-tail" slot={1} variant="release" />);
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
      }}
    >
      {tiles}
    </Box>
  );
}

interface GamesBodyArgs {
  readonly input: string;
  readonly setInput: (next: string) => void;
  readonly state: GamesPageState;
  readonly sentinelRef: React.RefObject<HTMLDivElement>;
}

function renderGamesBody(args: GamesBodyArgs): JSX.Element {
  const showBanner = args.state.status === 'failed';
  const visible = args.state.fetchedItems.slice(0, args.state.visibleCount);
  const showEmpty =
    args.state.status === 'succeeded' && visible.length === 0;
  return (
    <Stack spacing={2}>
      <SeoHead
        route="/games"
        pageTitle="Games"
        branding={brandingFixture.share}
        jsonLdType="WebSite"
      />
      <Typography variant="h1" component="h1">
        Games
      </Typography>
      <TextField
        fullWidth
        label="Search games"
        value={args.input}
        onChange={(e) => args.setInput(e.target.value)}
      />
      {showBanner ? <ApiUnavailableBanner /> : null}
      {visible.length > 0 ? <GamesGrid items={visible} /> : null}
      {showEmpty ? <EmptyResults /> : null}
      <Box ref={args.sentinelRef} sx={{ height: 1, width: '100%' }} aria-hidden="true" />
      {args.state.status === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Stack>
  );
}
